import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { sendPasswordResetRequest } from "../api/passwordResetApi";
import { auth, db } from "../firebase";

const AuthContext = createContext();

const getUserDocRef = (uid) => doc(db, "users", uid);

const mapUser = (authUser, profileData) => {
  if (!authUser) return null;
  const createdAt = profileData?.createdAt;
  const createdAtValue = createdAt && typeof createdAt.toDate === "function" ? createdAt.toDate().toISOString() : createdAt || "";
  return {
    id: authUser.uid,
    name: profileData?.name || authUser.displayName || "",
    email: authUser.email || "",
    phone: profileData?.phone || "",
    addresses: profileData?.addresses || [],
    avatar: profileData?.avatar || "",
    role: profileData?.role || "customer",
    createdAt: createdAtValue,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const ensureUserProfile = useCallback(async (authUser) => {
    const userRef = getUserDocRef(authUser.uid);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists()) {
      return snapshot.data();
    }

    const profileData = {
      name: authUser.displayName || "",
      email: authUser.email || "",
      phone: "",
      avatar: "",
      addresses: [],
      role: "customer",
      createdAt: new Date().toISOString(),
    };

    await setDoc(userRef, profileData);
    return profileData;
  }, []);

  const loadUser = useCallback(async (authUser) => {
    const profileData = await ensureUserProfile(authUser);
    const nextUser = mapUser(authUser, profileData);
    setUser(nextUser);
    return nextUser;
  }, [ensureUserProfile]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        await loadUser(authUser);
      } catch (err) {
        console.error("Failed to load user profile:", err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [loadUser]);

  const register = async (name, email, password, phone) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (credential.user) {
      await updateProfile(credential.user, { displayName: name });
      const userRef = getUserDocRef(credential.user.uid);
      const profileData = {
        name,
        email,
        phone: phone || "",
        avatar: "",
        addresses: [],
        role: "customer",
        createdAt: new Date().toISOString(),
      };
      await setDoc(userRef, profileData);
      const newUser = mapUser(credential.user, profileData);
      setUser(newUser);
      return newUser;
    }
    throw new Error("Failed to create user.");
  };

  const login = async (emailOrPhone, password) => {
    let email = emailOrPhone;
    if (!emailOrPhone.includes("@")) {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("phone", "==", emailOrPhone));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        email = querySnapshot.docs[0].data().email;
      }
    }

    const credential = await signInWithEmailAndPassword(auth, email, password);
    if (credential.user) {
      const nextUser = await loadUser(credential.user);
      return nextUser;
    }
    throw new Error("Unable to sign in.");
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const updateUser = async (updates) => {
    if (!auth.currentUser) throw new Error("User not authenticated");
    const userRef = getUserDocRef(auth.currentUser.uid);
    const nextData = { ...updates };
    if (updates.name) {
      await updateProfile(auth.currentUser, { displayName: updates.name });
    }
    await updateDoc(userRef, nextData);
    const nextUser = { ...user, ...nextData };
    setUser(nextUser);
    return nextUser;
  };

  const updatePassword = async (newPassword) => {
    if (!auth.currentUser) throw new Error("User not authenticated");
    await firebaseUpdatePassword(auth.currentUser, newPassword);
    return { success: true };
  };

  const sendPasswordReset = async (emailOrPhone) => {
    return sendPasswordResetRequest(emailOrPhone);
  };

  const addAddress = async (address) => {
    if (!auth.currentUser) throw new Error("User not authenticated");
    const addressId = Date.now().toString();
    const newAddress = { id: addressId, ...address };
    const nextAddresses = [...(user?.addresses || []), newAddress];
    const userRef = getUserDocRef(auth.currentUser.uid);
    await updateDoc(userRef, { addresses: nextAddresses });
    const nextUser = { ...user, addresses: nextAddresses };
    setUser(nextUser);
    return newAddress;
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    updateUser,
    updatePassword,
    sendPasswordReset,
    addAddress,
    isAuthenticated: !!user,
    isAdmin: !!user && user.role === "admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
